   // src/Home.js
   import React, { useState, useEffect } from 'react';
   import { useParams, useNavigate } from 'react-router-dom';
   import { getSubdomain } from '../utils/routingUtils';
   import { 
     Table, 
     TableBody, 
     TableCell, 
     TableContainer, 
     TableHead, 
     TableRow, 
     Paper,
     Box,
     Typography,
     IconButton,
     TextField,
     InputAdornment,
     Tooltip,
     CircularProgress,
     Menu,
     MenuItem,
     ListItemIcon,
     ListItemText
   } from '@mui/material';
   import { RemoveCircleOutline, AddCircleOutline, Search, Clear, MoreVert, Visibility, VisibilityOff, Delete, Settings, FileDownload } from '@mui/icons-material';

   const HighlightedText = ({ text = '', searchQuery = '' }) => {
       if (!searchQuery.trim() || !text) {
           return text || '-';
       }

       const parts = text.split(new RegExp(`(${searchQuery})`, 'gi'));
       
       return (
           <span>
               {parts.map((part, index) => (
                   part.toLowerCase() === searchQuery.toLowerCase() ? (
                       <span 
                           key={index}
                           style={{
                               backgroundColor: '#fff59d',
                               padding: '2px 0'
                           }}
                       >
                           {part}
                       </span>
                   ) : (
                       <span key={index}>{part}</span>
                   )
               ))}
           </span>
       );
   };

   function Responses() {
    const params = useParams();
    const { id: routeId } = params;
    
    // Check for subdomain-based routing
    const subdomain = getSubdomain();
    const id = (subdomain) ? subdomain : routeId;
    const navigate = useNavigate();
    const [responses, setResponses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedColumns, setExpandedColumns] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredIndices, setFilteredIndices] = useState([]);
    const [hiddenRows, setHiddenRows] = useState(new Set());
    const [anchorEl, setAnchorEl] = useState(null);
    const [activeRowIndex, setActiveRowIndex] = useState(null);
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'asc'
    });
    const [homeHover, setHomeHover] = useState(false);
    const [exportHover, setExportHover] = useState(false);
    
    useEffect(() => {
        setIsLoading(true);
        const token = sessionStorage.getItem('token');
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        fetch(`${API_URL}/api/event-invites/template/${id}/responses`, {
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        })
        .then((res) => {
            if (!res.ok) {
                if (res.status === 401) {
                    throw new Error('Unauthorized');
                }
                throw new Error('Network response was not ok');
            }
            return res.json();
        })
        .then((data) => {
            setResponses(data || []);
            setIsLoading(false);
        })
        .catch((error) => {
            console.error("Error fetching template:", error);
            navigate(`/invites/${id}/responses`);
        });
    }, [id, navigate]);

    useEffect(() => {
        if (!responses.length) return;
        
        const indices = [];
        const query = searchQuery.toLowerCase().trim();
        
        if (!query) {
            setFilteredIndices([...Array(responses[0].responses.length).keys()]);
            return;
        }

        responses[0].responses.forEach((_, rowIndex) => {
            const rowMatches = responses.some(question => {
                // Check main response
                const mainResponse = (question.responses[rowIndex]?.response || '').toLowerCase();
                if (mainResponse.includes(query)) {
                    return true;
                }
                
                // Check child responses if they exist
                if (question.childResponses) {
                    return question.childResponses.some(child => {
                        const childResponse = (child.responses[rowIndex]?.response || '').toLowerCase();
                        return childResponse.includes(query);
                    });
                }
                return false;
            });

            if (rowMatches) {
                indices.push(rowIndex);
            }
        });
        
        setFilteredIndices(indices);
    }, [searchQuery, responses]);

    const handleSort = (columnIndex, isChild = false, childIndex = null) => {
        let direction = 'asc';
        const sortKey = isChild ? `${columnIndex}-${childIndex}` : columnIndex;

        if (sortConfig.key === sortKey) {
            direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
        }

        setSortConfig({ key: sortKey, direction });

        const newResponses = [...responses];
        const baseResponses = newResponses[0].responses;
        const sortedIndices = [...baseResponses.keys()].sort((a, b) => {
            let valueA, valueB;

            if (isChild) {
                valueA = newResponses[columnIndex].childResponses[childIndex].responses[a]?.response || '';
                valueB = newResponses[columnIndex].childResponses[childIndex].responses[b]?.response || '';
            } else {
                valueA = newResponses[columnIndex].responses[a]?.response || '';
                valueB = newResponses[columnIndex].responses[b]?.response || '';
            }

            if (direction === 'asc') {
                return valueA.localeCompare(valueB);
            }
            return valueB.localeCompare(valueA);
        });

        // Reorder all responses based on the sorted indices
        newResponses.forEach(question => {
            const newResponses = sortedIndices.map(index => question.responses[index]);
            question.responses = newResponses;

            if (question.childResponses) {
                question.childResponses.forEach(child => {
                    const newChildResponses = sortedIndices.map(index => child.responses[index]);
                    child.responses = newChildResponses;
                });
            }
        });

        setResponses(newResponses);
    };

    const SortIndicator = ({ columnIndex, isChild = false, childIndex = null }) => {
        const sortKey = isChild ? `${columnIndex}-${childIndex}` : columnIndex;
        const isActive = sortConfig.key === sortKey;
        
        return (
            <Box 
                component="span" 
                sx={{ 
                    display: 'inline-flex',
                    alignItems: 'center',
                    ml: 1,
                    opacity: isActive ? 1 : 0.3,
                    transition: 'opacity 0.2s ease',
                    fontSize: '0.8rem',
                    userSelect: 'none'
                }}
            >
                {isActive && sortConfig.direction === 'desc' ? '▼' : '▲'}
            </Box>
        );
    };

    const handleExpandColumn = (columnIndex) => {
        const newExpanded = new Set(expandedColumns);
        if (newExpanded.has(columnIndex)) {
            newExpanded.delete(columnIndex);
        } else {
            newExpanded.add(columnIndex);
        }
        setExpandedColumns(newExpanded);
    };

    const handleClearSearch = () => {
        setSearchQuery('');
    };

    const handleRowActionClick = (event, rowIndex) => {
        setActiveRowIndex(rowIndex);
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleHideRow = () => {
        const newHiddenRows = new Set(hiddenRows);
        newHiddenRows.add(activeRowIndex);
        setHiddenRows(newHiddenRows);
        handleCloseMenu();
    };

    const handleShowRow = () => {
        const newHiddenRows = new Set(hiddenRows);
        newHiddenRows.delete(activeRowIndex);
        setHiddenRows(newHiddenRows);
        handleCloseMenu();
    };

    const handleDeleteRow = () => {
        const newResponses = [...responses];
        
        // Remove this response from each question
        newResponses.forEach(question => {
            question.responses = question.responses.filter((_, index) => index !== activeRowIndex);
            
            if (question.childResponses) {
                question.childResponses.forEach(child => {
                    child.responses = child.responses.filter((_, index) => index !== activeRowIndex);
                });
            }
        });
        
        setResponses(newResponses);
        
        // Update filtered indices
        setFilteredIndices(prevIndices => 
            prevIndices
                .filter(index => index !== activeRowIndex)
                .map(index => index > activeRowIndex ? index - 1 : index)
        );
        
        handleCloseMenu();
    };

    const getFilteredResponses = () => {
        if (!responses[0]?.responses) return [];
        return filteredIndices.filter(index => !hiddenRows.has(index));
    };

    const handleExportClick = async () => {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const token = sessionStorage.getItem('token');

        try {
            const response = await fetch(`${API_URL}/api/event-invites/export/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.status === 401) {
                navigate('/login');
                return;
            }

            if (!response.ok) {
                throw new Error('Export failed');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const now = new Date();
            const datetime = now.toISOString().slice(0, 16).replace('T', '_').replace(':', '-');
            const link = document.createElement('a');
            link.href = url;
            link.download = `InviteAvenue_${id}_${datetime}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export error:', error);
        }
    };

    if (isLoading) {
        return (
            <Box 
                sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    minHeight: '100vh'
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ padding: 3, position: 'relative' }}>
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 3 
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Tooltip title="Go Home" placement="bottom">
                        <button
                            onClick={() => navigate('/invites')}
                            onMouseEnter={() => setHomeHover(true)}
                            onMouseLeave={() => setHomeHover(false)}
                            onFocus={() => setHomeHover(true)}
                            onBlur={() => setHomeHover(false)}
                            style={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                background: '#fff',
                                border: 'none',
                                boxShadow: homeHover ? '0 4px 16px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.15)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 6,
                                transition: 'box-shadow 0.2s',
                            }}
                        >
                            <span
                                style={{
                                    display: 'flex',
                                    transition: 'transform 0.2s',
                                    transform: homeHover ? 'scale(1.07)' : 'none',
                                }}
                            >
                                <img
                                    src={require('../assets/favicon.png')}
                                    alt="Invite Avenue Logo"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                            </span>
                        </button>
                    </Tooltip>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 500,
                            color: '#2B4D6F',
                            fontSize: '1.5rem'
                        }}
                    >
                        RSVP Dashboard
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <TextField
                        placeholder="Search responses..."
                        variant="outlined"
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{
                            width: '300px',
                            backgroundColor: 'white',
                            borderRadius: '24px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '24px',
                                '& fieldset': {
                                    borderColor: 'transparent',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(0, 0, 0, 0.1)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#2B4D6F',
                                    borderWidth: '1px',
                                },
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={handleClearSearch}
                                        edge="end"
                                    >
                                        <Clear />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Tooltip title="Export to Excel" placement="bottom">
                        <button
                            onClick={handleExportClick}
                            onMouseEnter={() => setExportHover(true)}
                            onMouseLeave={() => setExportHover(false)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 16px',
                                borderRadius: '24px',
                                background: '#fff',
                                border: 'none',
                                boxShadow: exportHover ? '0 4px 12px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.1)',
                                cursor: 'pointer',
                                transition: 'box-shadow 0.2s',
                                fontSize: '0.875rem',
                                color: '#2B4D6F',
                                fontWeight: 500,
                            }}
                        ><FileDownload style={{ fontSize: '1.6rem' }} /></button>
                    </Tooltip>
                </Box>
            </Box>
            {responses.length > 0 ? (
                <TableContainer 
                    component={Paper} 
                    sx={{ 
                        boxShadow: 8,
                        borderRadius: 2,
                        overflow: 'auto',
                        '& .MuiPaper-root': {
                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                        },
                        '&::-webkit-scrollbar': {
                            height: '8px'
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f1f1f1'
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#888',
                            borderRadius: '4px',
                            '&:hover': {
                                backgroundColor: '#555'
                            }
                        }
                    }}
                >
                    <Table sx={{ minWidth: 650 }} aria-label="responses table">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                {responses.map((item, index) => (
                                    <React.Fragment key={index}>
                                        <TableCell 
                                            onClick={() => handleSort(index)}
                                            sx={{
                                                fontWeight: 'bold',
                                                fontSize: '1.1rem',
                                                color: '#1976d2',
                                                borderBottom: '2px solid #1976d2',
                                                padding: '16px',
                                                position: 'relative',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                                },
                                                '&::after': expandedColumns.has(index) ? {
                                                    content: '""',
                                                    position: 'absolute',
                                                    right: 0,
                                                    top: 0,
                                                    bottom: 0,
                                                    width: '1px',
                                                    backgroundColor: (theme) => theme.palette.divider
                                                } : {}
                                            }}
                                        >
                                            {item.title}
                                            <SortIndicator columnIndex={index} />
                                            {item.childResponses?.length > 0 && (
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleExpandColumn(index);
                                                    }}
                                                    sx={{ 
                                                        ml: 1, 
                                                        float: 'right',
                                                        color: 'inherit',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                                        }
                                                    }}
                                                >
                                                    {expandedColumns.has(index) ? 
                                                        <RemoveCircleOutline /> : 
                                                        <AddCircleOutline />
                                                    }
                                                </IconButton>
                                            )}
                                        </TableCell>
                                        {item.childResponses?.map((child, childIndex, array) => (
                                            <TableCell
                                                key={`${index}-${childIndex}`}
                                                onClick={() => handleSort(index, true, childIndex)}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    fontSize: '0.9rem',
                                                    color: '#1976d2',
                                                    borderBottom: '2px solid #1976d2',
                                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                    width: '140px',
                                                    transition: 'all 0.3s ease',
                                                    overflow: 'hidden',
                                                    maxWidth: expandedColumns.has(index) ? '140px' : '0px',
                                                    padding: expandedColumns.has(index) ? '12px' : '0px',
                                                    opacity: expandedColumns.has(index) ? 1 : 0,
                                                    position: 'relative',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(25, 118, 210, 0.08)'
                                                    },
                                                    '&::after': childIndex === array.length - 1 ? {
                                                        content: '""',
                                                        position: 'absolute',
                                                        right: 0,
                                                        top: 0,
                                                        bottom: 0,
                                                        width: '1px',
                                                        backgroundColor: (theme) => theme.palette.divider,
                                                        opacity: expandedColumns.has(index) ? 1 : 0,
                                                        transition: 'opacity 0.3s ease'
                                                    } : {}
                                                }}
                                            >
                                                {child.title}
                                                <SortIndicator columnIndex={index} isChild={true} childIndex={childIndex} />
                                            </TableCell>
                                        ))}
                                    </React.Fragment>
                                ))}
                                <TableCell 
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: '1.1rem',
                                        color: '#1976d2',
                                        borderBottom: '2px solid #1976d2',
                                        padding: '16px',
                                        width: '60px',
                                        textAlign: 'center'
                                    }}
                                >
                                    <Settings fontSize="small" />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getFilteredResponses().map((respondentIndex) => (
                                <TableRow
                                    key={respondentIndex}
                                    sx={{ 
                                        '&:nth-of-type(odd)': {
                                            backgroundColor: '#fafafa',
                                        },
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5',
                                            transition: 'background-color 0.2s ease'
                                        },
                                        '&:last-child td': { 
                                            borderBottom: 0 
                                        }
                                    }}
                                >
                                    {responses.map((question, questionIndex) => (
                                        <React.Fragment key={questionIndex}>
                                            <TableCell 
                                                sx={{
                                                    padding: '16px',
                                                    fontSize: '1rem',
                                                    position: 'relative',
                                                    '&::after': expandedColumns.has(questionIndex) ? {
                                                        content: '""',
                                                        position: 'absolute',
                                                        right: 0,
                                                        top: 0,
                                                        bottom: 0,
                                                        width: '1px',
                                                        backgroundColor: (theme) => theme.palette.divider
                                                    } : {}
                                                }}
                                            >
                                                <HighlightedText 
                                                    text={question.responses[respondentIndex]?.response}
                                                    searchQuery={searchQuery.trim()}
                                                />
                                            </TableCell>
                                            {question.childResponses?.map((child, childIndex, array) => (
                                                <TableCell 
                                                    key={`${questionIndex}-${childIndex}`}
                                                    sx={{
                                                        fontSize: '0.9rem',
                                                        backgroundColor: 'rgba(25, 118, 210, 0.02)',
                                                        width: '140px',
                                                        transition: 'all 0.3s ease',
                                                        overflow: 'hidden',
                                                        maxWidth: expandedColumns.has(questionIndex) ? '140px' : '0px',
                                                        padding: expandedColumns.has(questionIndex) ? '12px' : '0px',
                                                        opacity: expandedColumns.has(questionIndex) ? 1 : 0,
                                                        position: 'relative',
                                                        '&::after': childIndex === array.length - 1 ? {
                                                            content: '""',
                                                            position: 'absolute',
                                                            right: 0,
                                                            top: 0,
                                                            bottom: 0,
                                                            width: '1px',
                                                            backgroundColor: (theme) => theme.palette.divider,
                                                            opacity: expandedColumns.has(questionIndex) ? 1 : 0,
                                                            transition: 'opacity 0.3s ease'
                                                        } : {}
                                                    }}
                                                >
                                                    <HighlightedText 
                                                        text={child.responses[respondentIndex]?.response}
                                                        searchQuery={searchQuery.trim()}
                                                    />
                                                </TableCell>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                    <TableCell 
                                        align="center" 
                                        sx={{ 
                                            width: '60px',
                                            padding: '8px'
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleRowActionClick(e, respondentIndex)}
                                            sx={{
                                                color: '#1976d2',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(25, 118, 210, 0.08)'
                                                }
                                            }}
                                        >
                                            <MoreVert />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Typography 
                    variant="body1" 
                    sx={{ 
                        textAlign: 'center',
                        color: 'text.secondary',
                        mt: 3 
                    }}
                >
                    {searchQuery && filteredIndices.length === 0 ? 'No matching responses found' : 'No responses available'}
                </Typography>
            )}
            
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    elevation: 4,
                    sx: {
                        borderRadius: 2,
                        minWidth: '160px'
                    }
                }}
            >
                {activeRowIndex !== null && (
                    hiddenRows.has(activeRowIndex) ? (
                        <MenuItem onClick={handleShowRow}>
                            <ListItemIcon>
                                <Visibility fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary="Show Row" />
                        </MenuItem>
                    ) : (
                        <MenuItem onClick={handleHideRow}>
                            <ListItemIcon>
                                <VisibilityOff fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary="Hide Row" />
                        </MenuItem>
                    )
                )}
                <MenuItem onClick={handleDeleteRow}>
                    <ListItemIcon>
                        <Delete fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText primary="Delete Row" sx={{ color: 'error.main' }} />
                </MenuItem>
            </Menu>
        </Box>
    );
   }

   export default Responses;    